import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useClient } from '@/hooks/use-client';
import { useLoggedIn } from '@/hooks/use-logged-in';
import { useScopeUrl } from '@/hooks/use-scope-url';
import { scopedQueryKey, useAppQuery } from '@/queries/query';
import { compareId } from '@/utils/comparators';

import { queryKeys } from '../keys';

import type { Marker, PlApiClient } from 'pl-api';

// Query results and live events can arrive after a newer mark-read response.
// Merge at cache-write time so an in-flight request cannot restore unread items.
const mergeNotificationMarker = (previous: unknown, incoming: unknown): Marker | null => {
  const current = previous as Marker | null | undefined;
  const next = incoming as Marker | null;

  return current && (!next || compareId(current.last_read_id, next.last_read_id) > 0)
    ? current
    : next;
};

const saveNotificationsRead = async (
  client: PlApiClient,
  lastReadId: string,
  currentMarker?: Marker | null,
) => {
  if (client.features.notificationsMarkRead) {
    const readId =
      currentMarker && compareId(currentMarker.last_read_id, lastReadId) > 0
        ? currentMarker.last_read_id
        : lastReadId;

    // Akkoma rebuilds markers from seen flags when another notification arrives.
    // Repair those flags even if this browser already has an up-to-date marker.
    await client.notifications.markNotificationsAsRead(readId);
    return client.timelines.getMarkers(['notifications']);
  }

  if (currentMarker && compareId(currentMarker.last_read_id, lastReadId) >= 0) return;

  return client.timelines.saveMarkers({ notifications: { last_read_id: lastReadId } });
};

const useMarker = (timeline: 'home' | 'notifications', synchronize = false) => {
  const client = useClient();
  const { me } = useLoggedIn();

  return useAppQuery({
    queryKey: queryKeys.markers.timeline(timeline),
    queryFn: async () => (await client.timelines.getMarkers([timeline]))[timeline] ?? null,
    structuralSharing: timeline === 'notifications' ? mergeNotificationMarker : undefined,
    ...(synchronize
      ? ({
          staleTime: 0,
          refetchOnMount: 'always',
          refetchOnWindowFocus: 'always',
          refetchOnReconnect: 'always',
          refetchInterval: 30_000,
        } as const)
      : {}),
    enabled: !!me,
  });
};

const useNotificationsMarker = () => useMarker('notifications');

const useHomeTimelineMarker = () => useMarker('home');

const usePrefetchMarker = (timeline: 'home' | 'notifications') => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { me } = useLoggedIn();
  const scopeUrl = useScopeUrl();

  useEffect(() => {
    if (!me) return;
    queryClient.prefetchQuery({
      queryKey: scopedQueryKey(queryKeys.markers.timeline(timeline), scopeUrl),
      queryFn: async () => (await client.timelines.getMarkers([timeline]))[timeline] ?? null,
      structuralSharing: timeline === 'notifications' ? mergeNotificationMarker : undefined,
    });
  }, [me, timeline, client, queryClient, scopeUrl]);
};

const usePrefetchHomeTimelineMarker = () => usePrefetchMarker('home');

// Mounted once by the layout so badge consumers share one foreground poller.
// Streaming alone cannot recover read updates missed while another device is active.
const usePrefetchNotificationsMarker = () => useMarker('notifications', true);

export {
  mergeNotificationMarker,
  saveNotificationsRead,
  useNotificationsMarker,
  useHomeTimelineMarker,
  usePrefetchHomeTimelineMarker,
  usePrefetchNotificationsMarker,
};
